/* eslint-disable react/react-in-jsx-scope */
import { getAccessToken } from "helpers/jwt-token-access/accessToken"
import { useState, useEffect, useRef } from "react"
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import toastr from "toastr"

import B3Grid from "./B3Grid"

import Grid from "components/Common/Grid"
import "./../CompanysForm.css"
import DebtorGrid from "./DebtorGrid"
import MonitoredGrid from "./MonitoredGrid"
import RankingGrid from "./RankingGrid"
import XPGrid from "./XPGrid"
import "./XPGrid/XPGrid.css"

const CompaniesGrid = ({
  companyType,
  data,
  currentPage,
  setCurrentPage,
  totalPages,
  showList,
  setShowList,
  searchTerm,
  setSearchTerm,
  handleSearch,
  handleClearSearch,
}) => {
  const [companySelected, setCompanySelected] = useState(null)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const searchTimeoutRef = useRef(null)

  // Função para aplicar máscara de CNPJ
  const applyCnpjMask = (value) => {
    // Remove tudo que não é dígito
    const numericValue = value.replace(/\D/g, "")
    
    // Aplica a máscara progressivamente
    if (numericValue.length <= 2) {
      return numericValue
    } else if (numericValue.length <= 5) {
      return `${numericValue.slice(0, 2)}.${numericValue.slice(2)}`
    } else if (numericValue.length <= 8) {
      return `${numericValue.slice(0, 2)}.${numericValue.slice(2, 5)}.${numericValue.slice(5)}`
    } else if (numericValue.length <= 12) {
      return `${numericValue.slice(0, 2)}.${numericValue.slice(2, 5)}.${numericValue.slice(5, 8)}/${numericValue.slice(8)}`
    } else {
      return `${numericValue.slice(0, 2)}.${numericValue.slice(2, 5)}.${numericValue.slice(5, 8)}/${numericValue.slice(8, 12)}-${numericValue.slice(12, 14)}`
    }
  }

  // Handler para mudança no input de busca (CNPJ ou Nome Fantasia)
  const handleSearchChange = (e) => {
    const inputValue = e.target.value
    
    // Detectar se contém letras (é nome) ou apenas números (é CNPJ)
    const hasLetters = /[a-zA-Z]/.test(inputValue)
    
    if (hasLetters) {
      // É Nome Fantasia - sem máscara
      setSearchTerm(inputValue)
    } else {
      // É CNPJ - aplicar máscara
      const maskedValue = applyCnpjMask(inputValue)
      setSearchTerm(maskedValue)
    }
  }

  // UseEffect para monitorar mudanças e buscar automaticamente
  useEffect(() => {
    // Limpa o timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (searchTerm) {
      const hasLetters = /[a-zA-Z]/.test(searchTerm)
      
      if (hasLetters) {
        // É Nome Fantasia - buscar após 3 caracteres com delay de 1s
        if (searchTerm.length >= 3) {
          searchTimeoutRef.current = setTimeout(() => {
            handleSearch()
          }, 1000)
        }
      } else {
        // É CNPJ - buscar quando completo (14 dígitos)
        const numericValue = searchTerm.replace(/\D/g, "")
        if (numericValue.length === 14) {
          searchTimeoutRef.current = setTimeout(() => {
            handleSearch()
          }, 500)
        }
      }
    } else if (searchTerm === "") {
      // Se o campo foi limpo, recarregar a listagem normal
      searchTimeoutRef.current = setTimeout(() => {
        handleClearSearch()
      }, 300)
    }

    // Cleanup do timeout quando o componente desmontar ou searchTerm mudar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  const companyTypeLabels = {
    "empresas-b3": "B3",
    "empresas-devedoras": "Devedoras",
    "empresas-monitoradas": "Monitoradas",
    "empresas-ranking": "Ranking",
    "empresas-xp": "XP",
  }

  const handleUploadSpreadsheet = () => {
    setIsUploadModalOpen(true)
  }

  const handleDownloadTemplate = () => {
    // Mapear tipos de empresas para nomes de templates
    const templateMap = {
      "empresas-b3": "template_empresas_b3.csv",
      "empresas-devedoras": "template_empresas_devedoras.csv",
      "empresas-monitoradas": "template_empresas_monitoradas.csv",
      "empresas-ranking": "template_empresas_ranking.csv",
      "empresas-xp": "template_empresas_xp.csv",
    }
    
    const templateName = templateMap[companyType] || "template_empresas_xp.csv"
    
    // Criar link para download do template CSV local
    const link = document.createElement('a')
    link.href = `/templates/${templateName}`
    link.download = templateName
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    toastr.success('Template baixado com sucesso!', 'Sucesso')
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Validar se é um arquivo CSV (apenas CSV conforme documentação)
      const allowedTypes = [
        'text/csv',
        'application/csv',
        'text/plain'
      ]
      
      const allowedExtensions = ['.csv']
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        toastr.error('Por favor, selecione um arquivo CSV (.csv)', 'Erro')
        return
      }
      
      // Validar tamanho (máximo 5MB conforme documentação)
      if (file.size > 5 * 1024 * 1024) {
        toastr.error('O arquivo deve ter no máximo 5MB', 'Erro')
        return
      }
      
      setSelectedFile(file)
    }
  }

  const handleUploadFile = async () => {
    if (!selectedFile) {
      toastr.error('Por favor, selecione um arquivo', 'Erro')
      return
    }

    if (!companyType) {
      toastr.error('Tipo de empresa não selecionado', 'Erro')
      return
    }

    setIsUploading(true)
    
    try {
      const formData = new FormData()
      formData.append('csvFile', selectedFile, selectedFile.name)
      
      // Mapear tipos de empresas para endpoints da API
      const endpointMap = {
        "empresas-b3": "empresas-b3/import-csv",
        "empresas-devedoras": "empresas-devedoras/import-csv",
        "empresas-monitoradas": "empresas-monitoradas/import-csv",
        "empresas-ranking": "empresas-ranking/import-csv",
        "empresas-xp": "empresas-xp/import-csv",
      }
      
      const endpoint = endpointMap[companyType] || "empresas-xp/import-csv"
      const url = `${process.env.REACT_APP_API_INT_LEADS}/api/${endpoint}`
    
      console.log('Arquivo selecionado:', selectedFile)
      console.log('FormData entries:')
      for (let [key, value] of formData.entries()) {
        console.log(key, value)
      }

      const token = getAccessToken()
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          'x-access-token': token,
        },
      })
      
      const responseData = await response.json()
      
      if (response.ok) {
        // Mostrar resumo da importação se disponível
        if (responseData.resumo) {
          const { totalLinhas, empresasInseridas, empresasComErro } = responseData.resumo
          let message = `Importação concluída! ${empresasInseridas} empresa(s) inserida(s) de ${totalLinhas} linha(s).`
          if (empresasComErro > 0) {
            message += ` ${empresasComErro} empresa(s) com erro.`
          }
          toastr.success(message, 'Sucesso')
        } else {
          toastr.success(responseData.message || 'Planilha enviada e processada com sucesso!', 'Sucesso')
        }
        
        setIsUploadModalOpen(false)
        setSelectedFile(null)
        // Recarregar a lista de empresas
        if (setCurrentPage && companyType) {
          setCurrentPage({ companyType, page: 1, searchValue: searchTerm })
        }
      } else {
        // Tratar erros conforme documentação
        if (responseData.errors && Array.isArray(responseData.errors)) {
          const errorMessages = responseData.errors
            .map(error => {
              if (error.linha) {
                return `Linha ${error.linha}: ${error.erro}`
              }
              return error.erro || error
            })
            .join('; ')
          throw new Error(errorMessages)
        } else {
          throw new Error(responseData.message || 'Erro no processamento')
        }
      }
      
    } catch (error) {
      console.error('Erro no upload:', error)
      if (error.response) {
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Erro no servidor'
        toastr.error(`Erro: ${errorMessage}`, 'Erro')
      } else if (error.message) {
        toastr.error(`Erro: ${error.message}`, 'Erro')
      } else {
        toastr.error('Erro ao enviar a planilha. Tente novamente.', 'Erro')
      }
    } finally {
      setIsUploading(false)
    }
  }

  const toggleUploadModal = () => {
    setIsUploadModalOpen(!isUploadModalOpen)
    if (!isUploadModalOpen) {
      setSelectedFile(null)
    }
  }

  const singularCompanyTypeLabels = {
    "empresas-b3": "B3",
    "empresas-devedoras": "Devedora",
    "empresas-monitoradas": "Monitorada",
    "empresas-ranking": "Ranking",
    "empresas-xp": "XP",
  }

  return (
    <>
      {companyType && (
        // <Row lg="3" md="6" style={{ marginTop: 15 }}>
        <Grid marginTop={32}>
          <button
            type="button"
            onClick={() => {
              setShowList(true)
            }}
            className={
              showList === null
                ? "button-default-company-inactive"
                : showList
                ? "button-default-company"
                : "button-default-company-inactive"
            }
          >
            Ver lista de empresas {companyTypeLabels[companyType] || "Empresa"}
          </button>

          <button
            type="button"
            onClick={() => {
              setShowList(false)
              setCompanySelected(null)
            }}
            className={
              showList === null
                ? "button-default-company-inactive"
                : !showList
                ? "button-default-company"
                : "button-default-company-inactive"
            }
          >
            Adicionar uma nova empresa{" "}
            {singularCompanyTypeLabels[companyType] || "Empresa"}
          </button>
        </Grid>
        // </Row>
      )}

      {/* Botões de upload de planilha para todos os tipos */}
      {companyType && (
        <div className="spreadsheet-buttons-container" style={{ marginTop: 16 }}>
          <button
            type="button"
            onClick={handleUploadSpreadsheet}
            className="button-spreadsheet-upload"
          >
            <i className="bx bx-upload me-2"></i>
            Adicionar empresas por planilha
          </button>
          <button 
            type="button"
            onClick={handleDownloadTemplate}
            className="button-spreadsheet-download"
          >
            <i className="bx bx-download me-2"></i>
            Baixar planilha de exemplo
          </button>
        </div>
      )}

      {companyType && (
        <div className="spreadsheet-buttons-container" style={{ marginTop: 20 }}>
          <div className="search-box me-xxl-2 my-xxl-0 d-inline-block" style={{ flex: 1, maxWidth: "400px" }}>
            <div className="position-relative">
              <input
                onChange={handleSearchChange}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    handleSearch()
                  }
                }}
                id="search-bar"
                type="text"
                className="form-control"
                placeholder="Buscar por CNPJ ou Nome Fantasia"
                value={searchTerm || ""}
              />
              <i className="bx bx-search-alt search-icon"></i>
            </div>
          </div>
        </div>
      )}

      {companyType === "empresas-b3" && (
        <B3Grid
          data={data}
          currentPage={currentPage}
          setCurrentPage={page => setCurrentPage({ companyType, page, searchValue: searchTerm })}
          showList={showList}
          setShowList={setShowList}
          totalPages={totalPages}
          companySelected={companySelected}
          setCompanySelected={setCompanySelected}
        />
      )}

      {companyType === "empresas-devedoras" && (
        <DebtorGrid
          data={data}
          currentPage={currentPage}
          setCurrentPage={page => setCurrentPage({ companyType, page, searchValue: searchTerm })}
          showList={showList}
          setShowList={setShowList}
          companySelected={companySelected}
          setCompanySelected={setCompanySelected}
          totalPages={totalPages}
        />
      )}

      {companyType === "empresas-monitoradas" && (
        <MonitoredGrid
          data={data}
          currentPage={currentPage}
          setCurrentPage={page => setCurrentPage({ companyType, page, searchValue: searchTerm })}
          showList={showList}
          setShowList={setShowList}
          companySelected={companySelected}
          setCompanySelected={setCompanySelected}
          totalPages={totalPages}
        />
      )}

      {companyType === "empresas-ranking" && (
        <RankingGrid
          data={data}
          currentPage={currentPage}
          setCurrentPage={page => setCurrentPage({ companyType, page, searchValue: searchTerm })}
          showList={showList}
          setShowList={setShowList}
          companySelected={companySelected}
          setCompanySelected={setCompanySelected}
          totalPages={totalPages}
        />
      )}

      {companyType === "empresas-xp" && (
        <XPGrid
          data={data}
          currentPage={currentPage}
          setCurrentPage={page => setCurrentPage({ companyType, page, searchValue: searchTerm })}
          showList={showList}
          setShowList={setShowList}
          companySelected={companySelected}
          setCompanySelected={setCompanySelected}
          totalPages={totalPages}
        />
      )}

      {/* Modal de Upload de Planilha */}
      <Modal isOpen={isUploadModalOpen} toggle={toggleUploadModal} centered size="md">
        <ModalHeader toggle={toggleUploadModal}>
          <i className="bx bx-upload me-2"></i>
          Adicionar Empresas {companyTypeLabels[companyType] || ""} por Planilha
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">
              <strong>Selecione o arquivo:</strong>
            </label>
            <input
              type="file"
              className="form-control"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            <div className="form-text">
              Formato aceito: .csv (máximo 5MB)
            </div>
          </div>
          
          {selectedFile && (
            <div className="alert alert-info">
              <i className="bx bx-file me-2"></i>
              <strong>Arquivo selecionado:</strong> {selectedFile.name}
              <br />
              <small>Tamanho: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</small>
            </div>
          )}
          
          <div className="mt-3">
            <p className="text-muted">
              <i className="bx bx-info-circle me-2"></i>
              <strong>Instruções:</strong>
            </p>
            <ul className="text-muted small">
              <li>Baixe o template de exemplo para ver o formato correto</li>
              <li>Preencha os dados das empresas na planilha</li>
              <li>Certifique-se de que todos os campos obrigatórios estão preenchidos</li>
              <li>O arquivo será processado após o upload</li>
            </ul>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={toggleUploadModal}
            disabled={isUploading}
          >
            Cancelar
          </Button>
          <Button
            color="success"
            onClick={handleUploadFile}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? (
              <>
                <i className="bx bx-loader bx-spin me-2"></i>
                Enviando...
              </>
            ) : (
              <>
                <i className="bx bx-upload me-2"></i>
                Enviar Planilha
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CompaniesGrid
